import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Abfallart} from "../../api";
import DataTable from "../data/DataTable";
import {EntsorgerFilter} from "../entsorger/EntsorgerDataTable";
import useCustomFilters from "../data/useCustomFilters";

export type AbfallartFilter = {
    search?: string;
    filtered?: string;
};

export type Material = {
    id: string;
    key: string;
    name: string;
};

export type Props = Omit<DataRequest<AbfallartFilter>, "filter"> &
    Partial<Pick<DataRequest<AbfallartFilter>, "filter">>;

const tableName: string = "abfallarten"

export default function AbfallartenDataTable({...input}: Props) {
    const request = useDataRequest<AbfallartFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <AbfallartenResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<AbfallartFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<AbfallartFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-abfallart");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-abfallart");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function AbfallartenResults({
                                request,
                                setFilteredState,
                            }: {
    request: DataRequestState<AbfallartFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<EntsorgerFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadAbfallarten: DataLoader<AbfallartFilter, Abfallart> = useCallback(
        async (params) =>
            (await getApi()).abfallarten.sucheAbfallarten({...params}),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadAbfallarten}
            onRowClick={(row) => {
                navigate("/abfallarten/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Abfallart>> {
    const {t: translation} = useT("abfallart");

    return useMemo(
        () => [
            {
                field: "key",
                headerName: capitalize(translation('abfallart-attribute-eak-key')),
                flex: 1,
            },
            {
                field: "name",
                headerName: capitalize(translation('abfallart-attribute-name')),
                flex: 5,
            },
        ],
        [translation]
    );
}
